<template>
  <div>
    <div class="modal-header">
      <button type="button" class="close" data-dismiss="modal" aria-hidden="true" @click="closePrint">&times;
      </button>
      <h4 class="modal-title">{{trans('template.createPdf')}}</h4>
    </div>
    <div class="modal-body" v-if="!isInitializing">
      <v-layout row wrap id="select-template" style="margin: 0">
          <v-flex xs12>
            <div class="alert alert-warning" v-if="!printValid"><span>{{trans('template.noPagesInclude')}}</span></div>
            <div class="alert alert-warning" v-if="!prescriptionValid"><span>{{trans('template.noPrescriptionInclude')}}</span></div>
          </v-flex>
          <v-flex xs8>
            <label class="control-label mb-20">{{trans('template.fileTemplate')}}</label>
            <v-layout row wrap style="margin: 0" class="mb-20">
              <v-flex
                  v-for="template in data.availableTemplates"
                  :key="`${template}`"
                  xs6
                  class="template__option"
                  @mouseover="setTemporaryPreview(template)"
                  @mouseleave="resetTemporaryPreview()"
              >
                <p-radio name="type" type="radio" :value="template" v-model="selectedTemplate"
                         class="p-default p-round" color="success-o" :disabled="!supports(template)"
                         v-tooltip="{content: getTemplateTooltip(template), show: false, autoHide: false}"
                >
                    {{trans('template.types.' + template)}}
                    <i v-if="template === 'fruits'" class="fa fa-exclamation-circle" style="display: inline"/>
                </p-radio>
              </v-flex>
            </v-layout>

            <v-layout row wrap style="margin: 0">
              <v-flex xs7>
                <div v-show="pages.length > 0">
                  <div class="form-group">
                    <label for="pages-option" class="control-label">{{trans('template.includePages')}}</label>
                    <div id="pages-option">
                      <div v-for="page in pagesNotRelatedWithFood()" class="template__option">
                        <p-check v-if="page === 'prescription_page'"
                                 @change="pageSelectionChange"
                                 color="success"
                                 :value="page"
                                 v-model="selectedPages"
                                 :disabled="!hasPermission('prescription_show')"
                                 v-tooltip="{content: (!hasPermission('prescription_show')) ? trans('app.no_access_tooltip') : '', show: false, autoHide: false}"
                        >{{trans('template.pages.prescription_page')}}
                        </p-check>
                        <p-check v-else
                                 color="success"
                                 :value="page"
                                 v-model="selectedPages"
                        >{{trans('template.pages.' + page)}}
                        </p-check>
                        <permission-tooltip
                            v-if="page === 'prescription_page'"
                            :permission="hasPermission('prescription_show')"
                            :tooltip="{top: true, bottom: false, attach: false}"
                        >
                          <a :target="hasPermission('prescription_show') ? '_blank' : '_self'" :href="hasPermission('prescription_show') ? '/templates/prescriptions' : '#'">{{ `app.customize`| trans }}</a>
                        </permission-tooltip>
                        <icon-button
                            icon="library_add"
                            color="success"
                            v-if="page === 'list_page'"
                            :iconOptions="{small: true}"
                            :cls="'combined-patient-dialog-button'"
                            :tooltip="{ bottom: false, top: true, left: false, right: true, attach: true}"
                            :permission="hasPermission('menu_schema_print_combined_list_page')"
                            @click="openCombinePatientsDialog"
                          >
                          {{'app.combine_with_other_patient'|trans}}
                        </icon-button>
                        <div class="pl-20" v-if="page === 'prescription_page' && selectedPrescription">
                          <span class="font-italic">{{ selectedPrescription.name }}</span>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn icon small v-on="on" @click="changePrescription">
                                <v-icon small color="success">autorenew</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ `app.prescriptions.change_prescription`| trans }}</span>
                          </v-tooltip>
                        </div>
                      </div>
                      <p-check
                          color="success"
                          v-model="printDietByCheckboxChecked"
                      >
                        <template>
                          {{trans('template.templates')}}
                        </template>
                      </p-check>

                      <v-layout>
                      <v-flex xs6 v-for="(page, index) in pagesRelatedWithFood()" class="template__option pl-20" :key="index">

                        <permission-tooltip
                            v-if="page === 'print_by_meals' && hasPermission('print_by_meals') === false"
                            :permission="false"
                            :tooltip="{top: true, bottom: false, attach: false}"
                        >
                          <p-check
                              color="success"
                              :value="page"
                              v-model="selectedPagesRelatedWithFoodComputed"
                              :disabled="true"
                          >
                            {{trans('template.pages.' + page)}}
                          </p-check>
                        </permission-tooltip>

                        <p-check
                            v-else
                            color="success"
                            :value="page"
                            v-model="selectedPagesRelatedWithFoodComputed"
                            :disabled="!printDietByCheckboxChecked"
                        >
                          {{trans('template.pages.' + page)}}
                        </p-check>

                      </v-flex>
                      </v-layout>

                    </div>
                  </div>
                </div>
              </v-flex>
              <v-flex xs5>
                <div class="form-group">
                  <label for="energy-info" class="control-label">{{trans('template.options')}}</label>
                  <div id="energy-info" class="template__option">
                    <p-input type="checkbox" name="check" color="success" v-model="showEnergyAmount"
                             :disabled="disableShowEnergyAmount">
                      {{ trans('template.showEnergyInfo') }}
                    </p-input>
                    <i v-if="disableShowEnergyAmount" class="fa fa-question-circle"
                       v-tooltip="trans('template.no_nutrients_selected')"></i>
                    <a target="_blank" href="/settings/printing">{{ `app.customize`| trans }}</a>
                    <br>
                    <p-input type="checkbox" name="check" color="success" v-model="displayDates">
                      {{ trans('template.withDates') }}
                    </p-input>
                    <br>
                    <p-input type="checkbox" name="check" color="success" v-model="displayTimes">
                      {{ trans('template.withTimes') }}
                    </p-input>
                    <br>
                    <p-input v-if="hasPermission('creator_comments_edit')" type="checkbox" name="check" color="success"
                             v-model="displayComments">
                      {{ trans('template.withComments') }}
                    </p-input>
                  </div>
                </div>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs4 style="text-align:center">
            <v-layout align-center justify-center row>
              <v-flex xs6>
                <v-img
                    class="elevation-3"
                    v-if="templatePreview !== null"
                    :src="templatePreview"
                    :width="150"
                ></v-img>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      <v-layout v-if="selectedPages.includes('list_page')">
        <list-page-print v-if="!hideListPagePrint" ref="listPagePrint"></list-page-print>
      </v-layout>
    </div>
    <div class="modal-footer">
      <v-btn color="primary" flat @click="closePrint">
        {{ 'app.close' | trans }}
      </v-btn>
      <send-to-user-modal @send-email="sendEmail" @invalid-form="print" :sending-email="sendingEmail" :is-form-valid="validateForm"/>
      <v-btn
          :color="status || status === '' ? 'primary' : 'error'"
          :loading="isLoading || isInitializing"
          :disabled="sendingEmail || isLoading"
          @click="print"
      >
        {{ 'template.createPdf' | trans }}
      </v-btn>
    </div>
    <print-prescription-dialog
        @closed-without-selecting="deselectPrescription"
        @prescription-selected="prescriptionSelected"
        ref="printPrescriptionDialog"
    ></print-prescription-dialog>
    <combine-patients-dialog
        v-if="hasPermission('menu_schema_print_combined_list_page')"
        ref="combinePatientsDialog"
        @change="combinedMenusChanged"
    ></combine-patients-dialog>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import {dateFromTimestamp} from "../../../utils/dateFormatter";
import SendToUserModal from "./SendToUserModal.vue";
import {trans} from "../../../app";

export default {
  components: {
    'send-to-user-modal': SendToUserModal,
  },
  data() {
    return {
      isLoading: false,
      isInitializing: true,
      sendingEmail: false,
      status: '',
      email: null,
      data: {
        availableTemplates: null,
        supportedTemplates: null,
      },
      displayDates: true,
      displayTimes: true,
      displayComments: true,
      showEnergyAmount: true,
      selectedTemplate: null,
      pages: [],
      selectedPages: [],
      printValid: true,
      prescriptionValid: true,
      disableShowEnergyAmount: false,
      templatePreview: null,
      prescriptions: [],
      selectedPrescription: null,
      printDietByCheckboxChecked: false,
      selectedPagesRelatedWithFood: [],
      hideListPagePrint: false,
    };
  },
  created() {
    axios.get('/printer/' + this.menu.id).then((response) => {
      this.data = response.data;
      if (this.data.supportedTemplates.length > 0) {
        this.selectedTemplate = this.data.supportedTemplates[0];
        this.resetTemporaryPreview();
      }
    }).catch((error) => {
      this.sentryCaptureException(error);
    });

    this.fetchSettings().then(() => {
      if (this.$store.state.settings.nutrients_print.length === 0) {
        this.disableShowEnergyAmount = true;
        this.showEnergyAmount = false;
      }
    }).catch((error) => {
      this.sentryCaptureException(error);
    });

    window.Echo.private('pdf-created-' + window.user.id)
        .listen('PdfCreatedEvent', (e) => {
          this.isLoading = false;
          this.status = true;
          window.location.href = e.path;
        });
  },
  computed: {
    validateForm() {
      if (this.selectedPages.length === 0 && this.selectedPagesRelatedWithFoodComputed.length === 0) {
        return false;
      }

      return !(this.selectedPages.includes('prescription_page') && !this.selectedPrescription)
    },
    ...mapState(['menu']),
    ...mapGetters(['hasPermission', 'selectedCombinedMenus']),
    selectedPagesRelatedWithFoodComputed: {
      get() {
        return this.selectedPagesRelatedWithFood;
      },
      set(newValue) {
        if (this.selectedPagesRelatedWithFood.length > 0) {
          const pageToRemove = this.selectedPagesRelatedWithFood[0];
          this.selectedPagesRelatedWithFood = newValue.filter(page => page !== pageToRemove);

          return;
        }
        this.selectedPagesRelatedWithFood = newValue;
      }
    },
  },
  methods: {
    sendEmail(e) {
      this.email = e;
      this.print();
    },
    ...mapMutations([
      'hideModal',
      'setSelectedCombinedMenus',
    ]),
    ...mapActions(['fetchSettings']),
    fetchPages() {
      axios.get('/printer/pages/' + this.selectedTemplate).then((r) => {
        this.pages = r.data.pages;
        this.isInitializing = false;
      }).catch((error) => {
        this.sentryCaptureException(error);
      });
    },

    print() {
      this.isLoading = true;
      this.printValid = true;
      this.sendingEmail = this.email !== null;
      this.prescriptionValid = true;
      const pages = [];
      if (this.selectedPages.length === 0 && this.selectedPagesRelatedWithFoodComputed.length === 0) {
        this.isLoading = false;
        this.printValid = false;
        this.sendingEmail = false;
        return;
      }

      if (this.selectedPages.includes('prescription_page') && !this.selectedPrescription) {
        this.prescriptionValid = false;
        this.isLoading = false;
        this.sendingEmail = false;

        return;
      }

      this.selectedPages.forEach(p => {
        pages[this.pages.indexOf(p)] = p;
      });
      this.selectedPagesRelatedWithFoodComputed.forEach(p => {
        pages[this.pages.indexOf(p)] = p;
      });

      let ranges = []
      if (this.selectedPages.includes('list_page')) {
        if (this.hasPermission('menu_schema_print_ranges')) {
          ranges = this.$refs.listPagePrint.convertRangesToDates();
        } else {
          ranges = [
            this.menu.schema.days.map((day) => {
              return dateFromTimestamp(day.date).format()
            })
          ];
        }
      }

      const data = {
        template: this.selectedTemplate,
        pages: pages.filter(p => p),
        food_energy_info: this.showEnergyAmount,
        with_dates: this.displayDates,
        with_times: this.displayTimes,
        with_comments: this.hasPermission('creator_comments_edit') ? this.displayComments : false,
        range: ranges,
        prescription: this.selectedPrescription ? this.selectedPrescription.id : null,
        combinedMenus: this.selectedCombinedMenus.map((selectedMenu) => {
          return selectedMenu.uuid
        }),
      };

      if (this.email !== null) {
        data['email'] = this.email;
      }

      axios.post('/printer/print/' + this.menu.id, data)
          .then(() => {
            if (this.email !== null) {
              this.$toast.success(trans('app.template_sent'));
              this.sendingEmail = false;
              this.isLoading = false;
              this.status = true;
              this.closePrint();
            }
          })
          .catch(error => {
            this.sentryCaptureException(error);
            this.isLoading = false;
            this.status = false;
            this.sendingEmail = false;
          });
    },

    supports(template) {
      return this.data.supportedTemplates.includes(template);
    },
    setTemporaryPreview(template) {
      this.templatePreview = `${window.location.origin}/img/dashboard/templates/${template}.png`;
    },
    resetTemporaryPreview() {
      this.templatePreview = `${window.location.origin}/img/dashboard/templates/${this.selectedTemplate}.png`;
    },
    getPagesRelatedWithFood() {
      return ['days_page', 'print_by_meals',];
    },
    pagesRelatedWithFood() {
      return this.getPagesRelatedWithFood().filter(page => this.pages.includes(page));
    },
    pagesNotRelatedWithFood() {
      return this.pages.filter(page => this.getPagesRelatedWithFood().includes(page) === false);
    },
    pageSelectionChange(selectedPages) {
      if (selectedPages.includes('prescription_page')) {
        this.$refs.printPrescriptionDialog.show();
      } else {
        this.selectedPrescription = null;
      }
    },
    prescriptionSelected(prescription) {
      this.selectedPrescription = prescription;
    },
    deselectPrescription() {
      this.selectedPrescription = null;
      this.selectedPages = this.selectedPages.filter((page) => {
        return page !== 'prescription_page';
      });
    },
    changePrescription() {
      this.selectedPrescription = null;
      this.$refs.printPrescriptionDialog.show();
    },
    openCombinePatientsDialog() {
      this.$refs.combinePatientsDialog.show();
    },
    closePrint() {
      this.setSelectedCombinedMenus([])
      this.hideModal();
      this.email = null;
    },
    combinedMenusChanged() {
      this.hideListPagePrint = true;
      this.$nextTick(() => {
        this.hideListPagePrint = false;
      });
    },
    getTemplateTooltip(template) {
      if (this.supports(template) === false) {
        return trans('app.no_access_tooltip');
      }
      return '';
    }
  },

  watch: {
    selectedTemplate() {
      this.fetchPages();
    },
    selectedPrescription() {
      if (!!this.selectedPrescription) {
        this.prescriptionValid = true;
      }
    },
    printDietByCheckboxChecked() {
      if (this.printDietByCheckboxChecked === false) {
        this.selectedPagesRelatedWithFoodComputed = [];
      } else {
        this.selectedPagesRelatedWithFoodComputed = ['days_page'];
      }
    },
  },
};
</script>
